<script setup lang="ts">
  import '@/assets/calendar-legal-os.css'
  import CalendarSidebarEmptyState from '@/components/app/calendar/CalendarSidebarEmptyState.vue'
  import MiniMonthGrid from '@/components/app/calendar/MiniMonthGrid.vue'
  import { Button } from '@/components/ui/button'
  import { APPOINTMENTS_CALENDAR_SETUP_PATH } from '@/constants/calendar/external-links'
  import type { CalendarEventTypeEnum } from '@/types/generated'
  import type { MiniMonthCell } from '@/utils/calendar/date'
  import type { CalendarSidebarItem } from '@/types/calendar/sidebar'
  import { ChevronDown, ChevronLeft, ChevronRight, Loader2, Settings } from 'lucide-vue-next'
  import { computed } from 'vue'

  const props = defineProps<{
    miniMonthLabel: string
    miniMonthCells: MiniMonthCell[]
    weekdayLabels: string[]
    prevMonthLabel: string
    nextMonthLabel: string
    calendarsHeading: string
    settingsLabel: string
    expandFiltersLabel: string
    collapseFiltersLabel: string
    emptyTitle: string
    emptyDescription: string
    setupLabel: string
    loadFailedMessage: string
    retryLabel: string
    sidebarItems: CalendarSidebarItem[]
    selectedCalendarUuid: string | null
    expandedCalendarUuids: Record<string, boolean>
    isLoadingCalendars: boolean
    loadError: string | null
  }>()

  const emit = defineEmits<{
    'mini-prev': []
    'mini-next': []
    'select-date': [date: Date]
    'select-calendar': [uuid: string]
    'toggle-filters': [uuid: string]
    'toggle-filter': [uuid: string, type: CalendarEventTypeEnum]
    'open-settings': []
    retry: []
  }>()

  const hasCalendars = computed(() => props.sidebarItems.length > 0)

  const filterExpandLabel = (item: CalendarSidebarItem): string =>
    props.expandedCalendarUuids[item.uuid] ? props.collapseFiltersLabel : props.expandFiltersLabel
</script>

<template>
  <aside
    data-cy="calendar-sidebar"
    class="calendar-sidebar flex h-full min-h-0 w-[var(--calendar-sidebar-w)] shrink-0 flex-col overflow-hidden
      border-r border-[var(--cal-sidebar-line)] bg-[var(--cal-sidebar-bg)]"
  >
    <div class="shrink-0 px-2 pb-2 pt-3">
      <div class="mb-1 flex items-center justify-between gap-1 ps-1.5">
        <span
          class="block text-xs font-medium text-[var(--cal-muted)]"
          data-cy="calendar-mini-month-label"
        >
          {{ miniMonthLabel }}
        </span>

        <div class="flex items-center gap-1">
          <button
            type="button"
            class="inline-flex size-7 items-center justify-center rounded-full text-[var(--cal-muted)]
              hover:bg-[var(--cal-surface-hover)] hover:text-foreground focus:outline-none
              focus-visible:bg-[var(--cal-surface-hover)]"
            :aria-label="prevMonthLabel"
            data-cy="calendar-mini-prev-month"
            @click="emit('mini-prev')"
          >
            <ChevronLeft class="size-3.5 shrink-0" />
          </button>
          <button
            type="button"
            class="inline-flex size-7 items-center justify-center rounded-full text-[var(--cal-muted)]
              hover:bg-[var(--cal-surface-hover)] hover:text-foreground focus:outline-none
              focus-visible:bg-[var(--cal-surface-hover)]"
            :aria-label="nextMonthLabel"
            data-cy="calendar-mini-next-month"
            @click="emit('mini-next')"
          >
            <ChevronRight class="size-3.5 shrink-0" />
          </button>
        </div>
      </div>

      <MiniMonthGrid
        :cells="miniMonthCells"
        :weekday-labels="weekdayLabels"
        @select="emit('select-date', $event)"
      />
    </div>

    <div class="shrink-0 border-b border-[var(--cal-sidebar-line)]" />

    <div
      class="min-h-0 flex-1 overflow-y-auto px-2 py-3
        [scrollbar-color:var(--cal-scrollbar-thumb)_var(--cal-scrollbar-track)] [scrollbar-width:thin]
        [&::-webkit-scrollbar-thumb]:rounded-none
        [&::-webkit-scrollbar-thumb]:bg-[var(--cal-scrollbar-thumb)]
        [&::-webkit-scrollbar-track]:bg-[var(--cal-scrollbar-track)] [&::-webkit-scrollbar]:w-2"
      data-cy="calendar-sidebar-list"
    >
      <div
        v-if="isLoadingCalendars"
        class="flex items-center justify-center py-8"
        data-cy="calendar-sidebar-loading"
      >
        <Loader2 class="size-5 animate-spin text-[var(--cal-muted)]" />
      </div>

      <div
        v-else-if="loadError"
        class="flex flex-col items-center justify-center gap-3 px-2 py-8 text-center"
        data-cy="calendar-sidebar-error"
      >
        <p class="text-xs text-[var(--cal-muted)]">
          {{ loadFailedMessage }}
        </p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          data-cy="calendar-sidebar-retry"
          @click="emit('retry')"
        >
          {{ retryLabel }}
        </Button>
      </div>

      <CalendarSidebarEmptyState
        v-else-if="!hasCalendars"
        :title="emptyTitle"
        :description="emptyDescription"
        :setup-label="setupLabel"
        :setup-href="APPOINTMENTS_CALENDAR_SETUP_PATH"
      />

      <template v-else>
        <p class="mb-1.5 px-2 text-[10px] uppercase tracking-wide text-[var(--cal-muted)]">
          {{ calendarsHeading }}
        </p>
        <ul
          class="space-y-1"
          role="radiogroup"
          :aria-label="calendarsHeading"
        >
          <li
            v-for="item in sidebarItems"
            :key="item.uuid"
          >
            <div
              class="flex items-center rounded-md"
              :class="
                selectedCalendarUuid === item.uuid
                  ? 'bg-[var(--cal-surface)]'
                  : 'hover:bg-[var(--cal-surface-hover)]'
              "
            >
              <button
                type="button"
                role="radio"
                :aria-checked="selectedCalendarUuid === item.uuid"
                class="flex grow items-center gap-x-2 py-1.5 pe-1 ps-2 text-start text-xs text-foreground
                  focus:outline-none"
                :class="selectedCalendarUuid === item.uuid ? 'font-medium' : ''"
                :data-cy="`calendar-sidebar-item-${item.uuid}`"
                @click="emit('select-calendar', item.uuid)"
              >
                <span class="grow truncate">{{ item.label }}</span>
              </button>
              <button
                v-if="item.filters.length > 0"
                type="button"
                class="inline-flex size-7 items-center justify-center rounded-md text-[var(--cal-muted)]
                  hover:bg-[var(--cal-surface-hover)] hover:text-foreground focus:outline-none"
                :aria-expanded="!!expandedCalendarUuids[item.uuid]"
                :aria-label="filterExpandLabel(item)"
                :data-cy="`calendar-sidebar-filters-toggle-${item.uuid}`"
                @click="emit('toggle-filters', item.uuid)"
              >
                <ChevronDown
                  class="size-3.5 shrink-0 transition-transform"
                  :class="expandedCalendarUuids[item.uuid] ? 'rotate-180' : ''"
                />
              </button>
            </div>

            <div
              v-if="item.filters.length > 0"
              class="grid transition-[grid-template-rows] duration-200 ease-out"
              :class="expandedCalendarUuids[item.uuid] ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
            >
              <div class="overflow-hidden">
                <ul class="ms-4 mt-0.5 space-y-0.5">
                  <li
                    v-for="filter in item.filters"
                    :key="filter.id"
                  >
                    <label
                      :for="filter.id"
                      class="flex cursor-pointer select-none items-center gap-x-2 rounded-md px-2 py-1 text-xs text-foreground
                        hover:bg-[var(--cal-surface-hover)]"
                    >
                      <input
                        :id="filter.id"
                        :checked="filter.checked"
                        type="checkbox"
                        class="size-3.5 shrink-0 rounded-sm border-[var(--cal-layer-line)] bg-[var(--cal-layer)] shadow-sm
                          checked:border-primary checked:bg-primary focus:ring-0 focus:ring-offset-0"
                        @change="emit('toggle-filter', item.uuid, filter.type)"
                      />
                      <span class="truncate">{{ filter.label }}</span>
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </li>
        </ul>
      </template>
    </div>

    <div class="shrink-0 border-t border-[var(--cal-sidebar-line)] px-2 py-3">
      <button
        type="button"
        class="shadow-2xs inline-flex w-full items-center justify-center gap-x-1.5 rounded-md border
          border-[var(--cal-layer-line)] bg-[var(--cal-layer)] p-2 text-xs text-foreground
          hover:bg-[var(--cal-layer-hover)] focus:outline-none focus-visible:bg-[var(--cal-layer-hover)]"
        data-cy="calendar-sidebar-settings"
        :aria-label="settingsLabel"
        @click="emit('open-settings')"
      >
        <Settings class="size-3.5 shrink-0" />
        {{ settingsLabel }}
      </button>
    </div>
  </aside>
</template>
